import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, Checkbox, Container, Divider, Grid, InputAdornment, MenuItem, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { useAuth } from '../../../hooks/use-auth';
import { useMounted } from '../../../hooks/use-mounted';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../../../api/user-api';
import { SeverityPill } from '../../../components/severity-pill';
import { ArrowDropDown } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

const Activities = () => {
  const { user } = useAuth(); 
  const navigate = useNavigate();
  const isMounted = useMounted();
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [pagin, setPagin] = useState(0);
  const [limit, setLimit] = useState(25);
  const [selectedType, setSelectedType] = useState('');
  const [searchName, setSearchName] = useState("");
  const [startDate, setStartDate] = useState("01/01/2020");
  const [endDate, setEndDate] = useState(dayjs().format('DD/MM/YYYY'));
  const [sortOrder, setSortOrder] = useState("desc");
  const [courseOnly, setCourseOnly] = useState(false);

  const searchDebounceRef = useRef(null);

  // types calculé une seule fois quand logs change — pas à chaque render
  const types = useMemo(() => [...new Set(logs.map((log) => log.type))], [logs]);

  dayjs.locale('fr');

  // getLogs stable : prend les filtres explicitement, plus de closure sur les états
  const getLogs = useCallback(async (page = 1, lmt = 25, filters = {}) => {
    try {
      const data = await userApi.getAllActivities(user.admin_id, page, lmt, filters);
      if (isMounted()) {
        setLogs(data.data);
        setTotalLogs(data.total);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted, user.admin_id]);

  // Chargement initial — une seule fois
  useEffect(() => {
    getLogs(1, limit, { searchName, selectedType, startDate, endDate, sortOrder, courseOnly });
  }, []); // eslint-disable-line

  // Recherche : debounce 400ms — 1 seul appel API au lieu de 2
  const handleChangeSearch = (event) => {
    const value = event.target.value;
    setSearchName(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPagin(0);
      getLogs(1, limit, { searchName: value, selectedType, startDate, endDate, sortOrder, courseOnly });
    }, 400);
  };

  // Filtres immédiats — appellent getLogs avec la nouvelle valeur directement
  const handleTypeChange = (event) => {
    const value = event.target.value;
    setSelectedType(value);
    setPagin(0);
    getLogs(1, limit, { searchName, selectedType: value, startDate, endDate, sortOrder, courseOnly });
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPagin(0);
    getLogs(1, newLimit, { searchName, selectedType, startDate, endDate, sortOrder, courseOnly });
  };

  const handlePageChange = (_event, newPage) => {
    setPagin(newPage);
    getLogs(newPage + 1, limit, { searchName, selectedType, startDate, endDate, sortOrder, courseOnly });
  };

  const resetFilters = () => {
    const reset = { searchName: "", selectedType: '', startDate: "01/01/2008", endDate: dayjs().format('DD/MM/YYYY'), sortOrder: "desc", courseOnly: false };
    setSelectedType(reset.selectedType);
    setSearchName(reset.searchName);
    setStartDate(reset.startDate);
    setEndDate(reset.endDate);
    setSortOrder(reset.sortOrder);
    setCourseOnly(reset.courseOnly);
    setPagin(0);
    getLogs(1, limit, reset);
  };

  const paginatedLogs = logs;

  



  return (
    <>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 11
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 5 }}>
            <Grid
              container
              justifyContent="space-between"
              spacing={3}
            >
              <Grid item>
                <Typography variant="h4" sx={{fontFamily: "Montserrat"}}>
                  Activités
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <Divider/>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                m: -1,
                p: 3
              }}
            >
              <Box
                sx={{
                  m: 1,
                  maxWidth: '100%',
                  width: '25em'
                }}
              >
                <TextField
                  value={searchName}
                  onChange={handleChangeSearch}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  placeholder="Chercher un apprenant ou une description"
                />
              </Box>
              <Select
                value={selectedType}
                displayEmpty
                onChange={handleTypeChange}
                sx={{
                  minWidth: '10em',
                  maxHeight: '3.5em',
                  backgroundColor: '#F8F8F8',
                  borderRadius: '4px',
                  padding: '8px',
                  marginLeft: '1%',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#E8E8E8',
                  },
                  '&:focus': {
                    outline: 'none',
                    backgroundColor: '#F0F0F0',
                  },
                }}
                IconComponent={
                  ArrowDropDown
                }
              >
                <MenuItem value="">Tous les types</MenuItem>
                {types.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              <Box
                sx={{
                  ml: "1%",
                  width: '10em'
                }}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale='frFR'
                >
                  <DatePicker
                    label="Après cette date"
                    showTodayButton
                    focused
                    autoFocus
                    clearable
                    inputFormat='DD/MM/YYYY'
                    dateFormat='DD/MM/YYYY'
                    InputAdornmentProps={{ position: 'start', variant: 'standard' }}
                    onChange={(date) => {
                      if (dayjs(date).isValid()) {
                        const value = dayjs(date, 'MM/DD/YYYY').format('DD/MM/YYYY');
                        setStartDate(value);
                        setPagin(0);
                        getLogs(1, limit, { searchName, selectedType, startDate: value, endDate, sortOrder, courseOnly });
                      }
                    }}
                    renderInput={(params) =>
                      <TextField
                      {...params}
                      error={false}
                      />
                    }
                    value={dayjs(startDate, 'DD/MM/YYYY').format('MM/DD/YYYY')}
                    />
                </LocalizationProvider>
              </Box>
              <Box
                sx={{
                  ml: "1%",
                  width: '10em'
                }}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale='frFR'
                >
                  <DatePicker
                    label="Avant cette date"
                    showTodayButton
                    focused
                    autoFocus
                    clearable
                    inputFormat='DD/MM/YYYY'
                    dateFormat='DD/MM/YYYY'
                    InputAdornmentProps={{ position: 'start', variant: 'standard' }}
                    onChange={(date) => {
                      if (dayjs(date).isValid()) {
                        const value = dayjs(date, 'MM/DD/YYYY').format('DD/MM/YYYY');
                        setEndDate(value);
                        setPagin(0);
                        getLogs(1, limit, { searchName, selectedType, startDate, endDate: value, sortOrder, courseOnly });
                      }
                    }}
                    renderInput={(params) =>
                      <TextField
                      {...params}
                      error={false}
                      />
                    }
                    value={dayjs(endDate, 'DD/MM/YYYY').format('MM/DD/YYYY')}
                    />
                </LocalizationProvider>
              </Box>
              <Box
                sx={{
                  ml: "1%",
                }}
              >
                <Select
                  value={sortOrder}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSortOrder(value);
                    setPagin(0);
                    getLogs(1, limit, { searchName, selectedType, startDate, endDate, sortOrder: value, courseOnly });
                  }}
                >
                  <MenuItem value="desc">Date la plus proche</MenuItem>
                  <MenuItem value="asc">Date la plus éloignée</MenuItem>
                </Select>
              </Box>
              <Box
                sx={{
                  ml: "1%",
                }}
              >
                {/* <Typography sx={{maxWidth: '8em', fontSize: 15}}>
                  Activités de l'apprenant
                </Typography> */}
                <Checkbox
                  // sx={{ml: 5}}
                  checked={courseOnly}
                  onChange={(event) => {
                    const value = event.target.checked;
                    setCourseOnly(value);
                    setPagin(0);
                    getLogs(1, limit, { searchName, selectedType, startDate, endDate, sortOrder, courseOnly: value });
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: '1%',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={resetFilters}
                >
                  Réinitialiser les filtres
                </Button>
              </Box>
            </Box>
            <TablePagination
              component="div"
              count={totalLogs}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={pagin}
              labelRowsPerPage='Lignes par page :'
              rowsPerPage={limit}
              rowsPerPageOptions={[25, 50, 100]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
              }
             
            />
            <Table>
              <TableHead>
                <TableRow>
                <TableCell align="center">
                    <Typography fontWeight='Bold' fontSize={16}>
                      Apprenant
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight='Bold' fontSize={16}>
                      Type
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight='Bold' fontSize={16}>
                      Description
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight='Bold' fontSize={16}>
                      Date de création
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight='Bold' fontSize={16}>
                      ID du cours
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLogs
                .map((log, index) =>(
                  <TableRow key={log.id ?? index}>
                    <TableCell width="200" align='center'>
  <Typography 
    fontSize={16}
    sx={{
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:hover': {
        color: 'primary.main',
      }
    }}
    onClick={() => {
      const tenant = localStorage.getItem('tenant');
      navigate(`/${tenant}/dashboard/customers/${log.user_id}`);
    }}
  >
    {log.name}
  </Typography>
</TableCell>
                    <TableCell width="100" align="center">
                      <SeverityPill
                        color={log.color_type}
                      >
                        {log.type}
                      </SeverityPill>
                    </TableCell>
                    <TableCell width="500" align="center">
                      <div dangerouslySetInnerHTML={{ __html: log.description }} />
                    </TableCell>
                    <TableCell width="150" align="center">
                      {log.created_at}
                    </TableCell>
                    <TableCell width="150" align="center">
                      {log.courses_id}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalLogs}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={pagin}
              labelRowsPerPage='Lignes par page :'
              rowsPerPage={limit}
              rowsPerPageOptions={[25, 50, 100]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
              }
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};


export default Activities;
